/**
 * FLIP（First-Last-Invert-Play）で、レイアウト変更を滑らかに見せる。
 *
 * タイルサイズのスライダーは grid の `auto-fill` + `1fr` を動かすため、
 * 列数が変わる瞬間にタイルの幅が不連続に跳ぶ（例: 5列→4列で 247px → 313px）。
 * CSS transition では grid の再配置を補間できないので、変更前後の矩形を
 * 計測し、差分を transform で打ち消してから 0 へ戻すアニメーションを流す。
 *
 * 使い方: 変更を起こす直前に `snapshot()`、DOM が更新された直後
 * （useLayoutEffect）に `play()`。snapshot が無ければ play は何もしない。
 *
 * 連続操作中は前のアニメーションを取り消してから計測し直す。
 * getBoundingClientRect は進行中の transform を含むので、「見えている位置」
 * から次の位置へ繋がり、途中で跳ばない。
 */
import { useCallback, useRef, type RefObject } from 'react'

const DURATION_MS = 220
const EASING = 'cubic-bezier(.2, .7, .2, 1)'
/** 画面外のものは動かしても見えない。上下にこの余白を足した範囲だけ対象にする。 */
const VIEWPORT_MARGIN = 200

type Rect = { left: number; top: number; width: number; height: number }

const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect()
  return { left: r.left, top: r.top, width: r.width, height: r.height }
}

const nearViewport = (r: Rect): boolean =>
  r.top + r.height > -VIEWPORT_MARGIN && r.top < window.innerHeight + VIEWPORT_MARGIN

export function useFlip(ref: RefObject<HTMLElement | null>) {
  const before = useRef<Map<Element, Rect> | null>(null)

  const snapshot = useCallback(() => {
    const root = ref.current
    if (!root) return
    before.current = new Map(Array.from(root.children, (el) => [el, rectOf(el)]))
  }, [ref])

  const play = useCallback(() => {
    const map = before.current
    before.current = null
    const root = ref.current
    if (!map || !root) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    for (const el of Array.from(root.children)) {
      const prev = map.get(el)
      if (!prev || typeof el.animate !== 'function') continue
      // 先に進行中のものを止めてから測る。止めないと transform 込みの位置になる
      el.getAnimations().forEach((a) => a.cancel())
      const next = rectOf(el)
      if (next.width === 0 || next.height === 0) continue
      if (!nearViewport(prev) && !nearViewport(next)) continue

      const dx = prev.left - next.left
      const dy = prev.top - next.top
      const sx = prev.width / next.width
      const sy = prev.height / next.height
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(sx - 1) < 0.005 && Math.abs(sy - 1) < 0.005) {
        continue
      }
      el.animate(
        [
          { transformOrigin: '0 0', transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` },
          { transformOrigin: '0 0', transform: 'none' },
        ],
        { duration: DURATION_MS, easing: EASING },
      )
    }
  }, [ref])

  return { snapshot, play }
}
